/**
 * CreateInviteDialog Component
 * Dialog for creating new admin invitations
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminInvitesService } from "@/services/admin-invites.service";
import type { AdminRole } from "@/types/invite";

const inviteSchema = z.object({
  email: z.string().email("Invalid email address"),
  role: z.enum(["SuperAdmin", "BillingAdmin", "SupportAdmin"], {
    error: "Please select a role",
  }),
  expiresInDays: z.coerce
    .number()
    .min(1, "Expiration must be at least 1 day")
    .max(365, "Expiration cannot exceed 365 days")
    .default(7),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface CreateInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateInviteDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateInviteDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: "",
      role: "SupportAdmin",
      expiresInDays: 7,
    },
  });

  const onSubmit = async (data: InviteFormValues) => {
    setIsSubmitting(true);
    try {
      await adminInvitesService.createInvite({
        email: data.email,
        role: data.role as AdminRole,
        expiresInDays: data.expiresInDays,
      });

      toast.success("Invitation created successfully", {
        description: `An invitation has been sent to ${data.email}`,
      });

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create invite:", error);
      toast.error("Failed to create invitation", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Admin Invitation</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-4">
                <DialogDescription>
                  Send an invitation to grant admin access. The recipient will
                  receive an email with instructions to complete their
                  registration.
                </DialogDescription>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="admin@example.com"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The email address of the person to invite
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Admin Role</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isSubmitting}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="SuperAdmin">
                            Super Admin
                          </SelectItem>
                          <SelectItem value="BillingAdmin">
                            Billing Admin
                          </SelectItem>
                          <SelectItem value="SupportAdmin">
                            Support Admin
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        The level of access to grant to the invitee
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="expiresInDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expires In (Days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          placeholder="7"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Number of days before the invitation expires (1-365)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create Invitation"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
